import { Music, Users, Package, DollarSign, XCircle, Edit2, Eye, Clock, BadgeCheck, CheckCircle, Plus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { WebhookHealthWidget } from './WebhookHealthWidget';
import { LaunchControls } from './LaunchControls';

export function OverviewTab({ stats, artists, orders, users, pendingArtists, verificationRequests, statsDetailView, setStatsDetailView, openArtistDialog, handleApproveArtist, handleToggleVerification, handleViewUserDetails, setEditingArtist, setArtistForm, setArtistDialogOpen }) {
  return (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-surface border border-[#262626] rounded-xl p-6 cursor-pointer hover:border-lime/50 transition-colors" onClick={() => setStatsDetailView('artists')} data-testid="stats-artists">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-lime/10 rounded-xl flex items-center justify-center"><Music className="w-6 h-6 text-lime" /></div>
            <div><p className="text-2xl font-mono font-bold">{stats?.total_artists || 0}</p><p className="text-sm text-[#A1A1AA]">Total Artists</p></div>
          </div>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-6 cursor-pointer hover:border-blue-500/50 transition-colors" onClick={() => setStatsDetailView('users')} data-testid="stats-users">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-blue-500" /></div>
            <div><p className="text-2xl font-mono font-bold">{stats?.total_users || 0}</p><p className="text-sm text-[#A1A1AA]">Users</p></div>
          </div>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-6 cursor-pointer hover:border-purple-500/50 transition-colors" onClick={() => setStatsDetailView('orders')} data-testid="stats-orders">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center"><Package className="w-6 h-6 text-purple-500" /></div>
            <div><p className="text-2xl font-mono font-bold">{stats?.total_orders || 0}</p><p className="text-sm text-[#A1A1AA]">Total Orders</p></div>
          </div>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center"><DollarSign className="w-6 h-6 text-green-500" /></div>
            <div><p className="text-2xl font-mono font-bold">${stats?.total_platform_earnings?.toFixed(2) || '0.00'}</p><p className="text-sm text-[#A1A1AA]">Revenue (10%)</p></div>
          </div>
        </div>
      </div>

      {/* Launch Controls */}
      <LaunchControls />

      {/* System Health */}
      <WebhookHealthWidget />

      {/* Stats Detail View */}
      {statsDetailView && (
        <div className="bg-surface border border-[#262626] rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold flex items-center gap-2">
              {statsDetailView === 'artists' && <><Music className="w-5 h-5 text-lime" /> All Artists</>}
              {statsDetailView === 'users' && <><Users className="w-5 h-5 text-blue-500" /> All Users</>}
              {statsDetailView === 'orders' && <><Package className="w-5 h-5 text-purple-500" /> All Orders</>}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setStatsDetailView(null)}><XCircle className="w-4 h-4" /></Button>
          </div>
          {statsDetailView === 'artists' && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {artists.map((artist) => (
                <div key={artist.id} className="flex items-center justify-between p-3 bg-surface-highlight rounded-lg">
                  <div className="flex items-center gap-3">
                    <img src={artist.image_url || 'https://via.placeholder.com/40'} alt={artist.name} className="w-10 h-10 rounded-full object-cover" />
                    <div><p className="font-medium">{artist.name}</p><p className="text-xs text-[#666]">{artist.email || 'No email'} &bull; ${artist.price}</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${artist.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{artist.is_active ? 'Active' : 'Inactive'}</span>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingArtist(artist); setArtistForm(artist); setArtistDialogOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {statsDetailView === 'users' && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-surface-highlight rounded-lg">
                  <div><p className="font-medium">{user.name}</p><p className="text-xs text-[#666]">{user.email} &bull; {user.role}</p></div>
                  <div className="flex items-center gap-3">
                    <div className="text-right"><p className="text-sm">{user.total_orders || 0} orders</p><p className="text-xs text-[#666]">${(user.total_spent || 0).toFixed(2)} spent</p></div>
                    <Button size="sm" variant="ghost" onClick={() => handleViewUserDetails(user)}><Eye className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {statsDetailView === 'orders' && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {orders.map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 bg-surface-highlight rounded-lg">
                  <div><p className="font-medium">{order.artist_name}</p><p className="text-xs text-[#666]">From: {order.user_name} &bull; ${order.price}</p></div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${order.status === 'completed' ? 'bg-green-500/20 text-green-400' : order.status === 'delivered' ? 'bg-blue-500/20 text-blue-400' : order.status === 'accepted' ? 'bg-yellow-500/20 text-yellow-400' : order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>{order.status}</span>
                    <p className="text-xs text-[#666]">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Approvals */}
      {pendingArtists.length > 0 && (
        <div className="bg-surface border border-yellow-500/30 rounded-xl p-6 mb-8">
          <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-yellow-500" />Pending Artist Approvals</h3>
          <div className="space-y-3">
            {pendingArtists.map((artist) => (
              <div key={artist.id} className="flex items-center justify-between p-4 bg-surface-highlight rounded-lg">
                <div><p className="font-medium">{artist.name}</p><p className="text-sm text-[#A1A1AA]">{artist.email || 'No email added'}</p></div>
                <Button onClick={() => handleApproveArtist(artist.id)} size="sm" className="btn-primary" data-testid={`approve-artist-${artist.id}`}><CheckCircle className="w-4 h-4 mr-2" />Approve</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Requests */}
      {verificationRequests.length > 0 && (
        <div className="bg-surface border border-blue-500/30 rounded-xl p-6 mb-8">
          <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-blue-500" />Verification Requests</h3>
          <div className="space-y-3">
            {verificationRequests.map((artist) => (
              <div key={artist.id} className="flex items-center justify-between p-4 bg-surface-highlight rounded-lg">
                <div className="flex items-center gap-3">
                  {artist.image_url && <img src={artist.image_url} alt={artist.name} className="w-10 h-10 rounded-full object-cover" />}
                  <div><p className="font-medium">{artist.name}</p><p className="text-sm text-[#A1A1AA]">${artist.price} &bull; {artist.total_orders} orders</p></div>
                </div>
                <Button onClick={() => handleToggleVerification(artist.id)} size="sm" className="bg-blue-500 hover:bg-blue-600 text-white" data-testid={`verify-artist-${artist.id}`}><BadgeCheck className="w-4 h-4 mr-2" />Verify</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-surface border border-[#262626] rounded-xl p-6">
        <h3 className="font-heading font-bold mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => openArtistDialog()} variant="outline" className="border-[#262626]"><Plus className="w-4 h-4 mr-2" />Add New Artist</Button>
        </div>
      </div>
    </>
  );
}
